import React, { useState, useEffect } from 'react'
import styles from './ChatRoomModal.module.scss'
import TextChat from '../../../../../components/TextChat/TextChat'
import VoiceChat from '../../../../../components/VoiceChat/VoiceChat'
import { CloseIcon, PeopleIcon, SettingsIcon, LeaveIcon, MicrophoneIcon, GlobeIcon, LockIcon } from '../../../../../components/CustomIcons'
import CommunityService from '../../../../../service/CommunityService'
import { getApiUrl } from '../../../../../config/websocket'

const ChatRoomModal = ({ room, currentUser, onClose, isOwner }) => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSettings, setShowSettings] = useState(false)
  const [inviteCode, setInviteCode] = useState(room?.invite_code || '')
  const [copiedInvite, setCopiedInvite] = useState(false)
  const [memberSearch, setMemberSearch] = useState('')
  const [mobileTab, setMobileTab] = useState('chat')
  // Mobile info panel
  const [showInfoPanel, setShowInfoPanel] = useState(false)
  const [infoPanelSection, setInfoPanelSection] = useState(null) // null | 'media' | 'audio' | 'members'
  const [infoPanelMedia, setInfoPanelMedia] = useState([])
  const [infoPanelAudio, setInfoPanelAudio] = useState([])
  const [infoPanelLoading, setInfoPanelLoading] = useState(false)
  const [infoPanelLightbox, setInfoPanelLightbox] = useState(null)
  const [membersExpanded, setMembersExpanded] = useState(false)

  useEffect(() => {
    if (room) {
      loadMembers()
      setInviteCode(room.invite_code || '')
    }
  }, [room])

  const loadMembers = async () => {
    try {
      setLoading(true)
      if (isOwner) {
        const response = await CommunityService.getChatMembers(room.slug)
        setMembers(response.data || [])
      } else {
        const response = await CommunityService.getChatRoom(room.slug)
        setMembers(response.data.members || [])
      }
    } catch (error) {
      console.error('Ошибка загрузки участников:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleKickMember = async (userId) => {
    if (!window.confirm('Удалить участника из чата?')) return
    try {
      const result = await CommunityService.kickChatMember(room.slug, userId)
      if (result.data.new_invite_code) setInviteCode(result.data.new_invite_code)
      await loadMembers()
    } catch (error) {
      console.error('Ошибка при удалении участника:', error)
    }
  }

  const handleRegenerateInvite = async () => {
    if (!window.confirm('Обновить код приглашения? Старый код перестанет работать.')) return
    try {
      const result = await CommunityService.regenerateInviteCode(room.slug)
      setInviteCode(result.data.invite_code)
    } catch (error) {
      console.error('Ошибка обновления кода:', error)
    }
  }

  const handleCopyInvite = () => {
    navigator.clipboard.writeText(inviteCode)
    setCopiedInvite(true)
    setTimeout(() => setCopiedInvite(false), 2000)
  }

  const handleLeaveRoom = async () => {
    try {
      await CommunityService.leaveChatRoom(room.slug)
      onClose()
    } catch (error) {
      console.error('Ошибка при выходе из чата:', error)
    }
  }

  // ─── Mobile info panel helpers ────────────────────────────────────────────
  const _isPanelAudio = (name) => {
    if (!name) return false
    return /\.(ogg|mp3|wav|m4a|opus|aac)$/i.test(name) || (/\.webm$/i.test(name) && /^audio_/i.test(name))
  }
  const _isPanelMedia = (name) => {
    if (!name) return false
    if (/^video_message_/i.test(name)) return true
    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/i.test(name)) return true
    if (/\.(mp4|mov|avi|mkv|m4v)$/i.test(name)) return true
    if (/\.webm$/i.test(name) && !_isPanelAudio(name)) return true
    if (/\.(pdf|doc|docx|xls|xlsx|zip|txt|7z|rar)$/i.test(name)) return true
    return false
  }

  const loadInfoPanelMedia = async () => {
    if (infoPanelMedia.length > 0 || infoPanelAudio.length > 0) return
    setInfoPanelLoading(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(getApiUrl(`/api/community/chat-rooms/${room.slug}/messages/?limit=100`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      const data = await res.json()
      const msgs = data.results || []
      const media = [], audio = []
      for (const msg of msgs) {
        const content = msg.content || ''
        if (!content.startsWith('__FILE__:')) continue
        try {
          const fi = JSON.parse(content.slice(9))
          if (_isPanelAudio(fi.name)) audio.push(fi)
          else if (_isPanelMedia(fi.name)) media.push(fi)
        } catch (_) {}
      }
      setInfoPanelMedia(media.reverse())
      setInfoPanelAudio(audio.reverse())
    } catch (_) {} finally {
      setInfoPanelLoading(false)
    }
  }

  const openInfoPanel = () => {
    setShowInfoPanel(true)
    setInfoPanelSection(null)
    loadInfoPanelMedia()
  }

  if (!room) return null

  const getRoomTypeIcon = () => {
    switch (room.room_type) {
      case 'public': return <GlobeIcon size={20} />
      case 'private': return <LockIcon size={20} />
      case 'voice': return <MicrophoneIcon size={20} />
      default: return <PeopleIcon size={20} />
    }
  }

  const getRoomTypeName = () => {
    switch (room.room_type) {
      case 'public': return 'Публичный'
      case 'private': return 'Приватный'
      case 'voice': return 'Голосовой'
      default: return 'Чат'
    }
  }

  const filteredMembers = members.filter(m =>
    !memberSearch.trim() || (m.user || m).username?.toLowerCase().includes(memberSearch.toLowerCase())
  )

  return (
    <div className={styles.fullPage}>
      {/* Header */}
      <div className={styles.header}>
        <div
          className={`${styles.headerLeft} ${styles.headerLeftTappable}`}
          onClick={openInfoPanel}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openInfoPanel() }}
        >
          <div className={styles.roomTypeIcon}>{getRoomTypeIcon()}</div>
          <div className={styles.roomInfo}>
            <h2 className={styles.roomName}>{room.name}</h2>
            <div className={styles.roomMeta}>
              <span className={`${styles.roomType} ${styles[room.room_type]}`}>{getRoomTypeName()}</span>
              <span className={styles.category}>{room.category_name}</span>
            </div>
          </div>
        </div>
        <div className={styles.headerActions}>
          {isOwner && (
            <button className={`${styles.settingsBtn} ${styles.desktopOnly}`} onClick={(e) => { e.stopPropagation(); setShowSettings(!showSettings); }} title="Управление чатом">
              <SettingsIcon size={18} />
            </button>
          )}
          <button
            className={`${styles.infoPanelToggleBtn} ${styles.desktopOnly}${showInfoPanel ? ' ' + styles.infoPanelToggleBtnActive : ''}`}
            onClick={() => setShowInfoPanel(v => !v)}
            title={showInfoPanel ? 'Скрыть панель' : 'Информация о чате'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </button>
          <button className={styles.closeBtn} onClick={onClose} title="Назад к чатам">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile tab bar — hidden on mobile (info panel replaces it) */}
      <div className={styles.mobileTabs}>
        <button className={`${styles.mobileTab} ${mobileTab === 'chat' ? styles.mobileTabActive : ''}`} onClick={() => setMobileTab('chat')}>
          {(room.room_type === 'voice' || room.room_type === 'voice_private') ? '🎙 Общение' : '💬 Чат'}
        </button>
        <button className={`${styles.mobileTab} ${mobileTab === 'members' ? styles.mobileTabActive : ''}`} onClick={() => setMobileTab('members')}>
          👥 Участники {members.length > 0 && <span className={styles.mobileTabCount}>{members.length}</span>}
        </button>
      </div>

      {/* Main Content */}
      <div className={`${styles.mainContent}${showInfoPanel ? ' ' + styles.infoPanelVisible : ''}`}>
        {/* Chat area — always shown on mobile */}
        <div className={styles.chatSection}>
          {(room.room_type === 'voice' || room.room_type === 'voice_private') ? (
            <VoiceChat roomSlug={room.slug} currentUser={currentUser} />
          ) : (
            <TextChat roomSlug={room.slug} currentUser={currentUser} />
          )}
        </div>

        {/* Members sidebar — collapses to avatar strip on desktop, always expanded on mobile */}
        <div className={`${styles.membersSection} ${(membersExpanded || mobileTab === 'members') ? styles.membersSectionExpanded : styles.membersSectionCollapsed} ${mobileTab !== 'members' ? styles.mobileHidden : ''}`}>

          {/* Collapse toggle handle */}
          <button
            className={styles.membersToggle}
            onClick={() => setMembersExpanded(v => !v)}
            title={membersExpanded ? 'Свернуть участников' : 'Развернуть участников'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ transform: membersExpanded ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.25s' }}>
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
            </svg>
          </button>

          {/* Collapsed: avatar stack (desktop only) */}
          {!membersExpanded && mobileTab !== 'members' && (
            <div className={styles.membersAvatarStack} data-lenis-prevent>
              <div className={styles.membersCountBadge}>{members.length}</div>
              {members.slice(0, 8).map((member) => {
                const memberUser = member.user || member
                const avatarUrl = memberUser?.avatar
                  ? (memberUser.avatar.startsWith('http') ? memberUser.avatar : `https://security.ncore.kg${memberUser.avatar}`)
                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(memberUser?.username || 'U')}&background=7C3AED&color=fff&bold=true`
                const isCurrentUser = memberUser?.id === currentUser?.id
                return (
                  <div key={member.id} className={`${styles.stackAvatar} ${isCurrentUser ? styles.stackAvatarSelf : ''}`} title={memberUser?.username}>
                    <img src={avatarUrl} alt={memberUser?.username}
                      onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(memberUser?.username || 'U')}&background=7C3AED&color=fff&bold=true` }} />
                    {memberUser?.is_online && <div className={styles.stackOnlineDot} />}
                  </div>
                )
              })}
              {members.length > 8 && (
                <div className={styles.stackMore}>+{members.length - 8}</div>
              )}
            </div>
          )}

          {/* Expanded: full panel (desktop when expanded, always on mobile members tab) */}
          {(membersExpanded || mobileTab === 'members') && (
            <>
              <div className={styles.membersHeader}>
                <PeopleIcon size={16} />
                <h3>Участники <span className={styles.memberCount}>{members.length}</span></h3>
              </div>

              <input
                className={styles.memberSearch}
                type="text"
                placeholder="Поиск..."
                value={memberSearch}
                onChange={e => setMemberSearch(e.target.value)}
              />

              {isOwner && (room.room_type === 'private' || room.room_type === 'voice_private') && inviteCode && (
                <div className={styles.invitePanel}>
                  <div className={styles.invitePanelLabel}>🔗 Приглашение</div>
                  <div className={styles.invitePanelRow}>
                    <code className={styles.invitePanelCode}>{inviteCode}</code>
                    <button className={styles.inviteCopyBtn} onClick={handleCopyInvite} title="Копировать">
                      {copiedInvite ? '✓' : '📋'}
                    </button>
                    <button className={styles.inviteRegenBtn} onClick={handleRegenerateInvite} title="Обновить">🔄</button>
                  </div>
                </div>
              )}

              <div className={styles.membersList} data-lenis-prevent>
                {loading ? (
                  <div className={styles.loading}>Загрузка...</div>
                ) : filteredMembers.length === 0 ? (
                  <div className={styles.empty}>Нет участников</div>
                ) : (
                  filteredMembers.map((member) => {
                    const memberUser = member.user || member
                    const avatarUrl = memberUser?.avatar
                      ? (memberUser.avatar.startsWith('http') ? memberUser.avatar : `https://security.ncore.kg${memberUser.avatar}`)
                      : `https://ui-avatars.com/api/?name=${encodeURIComponent(memberUser?.username || 'User')}&background=7C3AED&color=fff&bold=true`
                    const isRoomOwner = memberUser?.id === room.owner?.id
                    const isCurrentUser = memberUser?.id === currentUser?.id

                    return (
                      <div key={member.id} className={`${styles.memberCard} ${isCurrentUser ? styles.currentUser : ''}`}>
                        <div className={styles.memberAvatarWrap}>
                          <img
                            src={avatarUrl}
                            alt={memberUser?.username}
                            className={styles.memberAvatar}
                            onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(memberUser?.username || 'User')}&background=7C3AED&color=fff&bold=true` }}
                          />
                          {memberUser?.is_online && <div className={styles.memberOnlineDot} />}
                        </div>
                        <div className={styles.memberInfo}>
                          <div className={styles.memberName}>
                            {memberUser?.username}
                            {isCurrentUser && <span className={styles.youBadge}>вы</span>}
                          </div>
                          <div className={styles.memberRole}>
                            {isRoomOwner && <span className={styles.ownerBadge}>👑</span>}
                            {member.role === 'moderator' && <span className={styles.modBadge}>🛡️ Мод</span>}
                            {member.role === 'admin' && <span className={styles.adminBadge}>⚔️ Адм</span>}
                            {!isRoomOwner && member.role === 'member' && <span className={styles.regularBadge}>Участник</span>}
                          </div>
                        </div>
                        {member.is_muted && <span className={styles.mutedBadge} title="Заглушен">🔇</span>}
                        {isOwner && !isRoomOwner && !isCurrentUser && (
                          <button className={styles.kickBtn} onClick={() => handleKickMember(memberUser.id)} title="Удалить из чата">✕</button>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showSettings && isOwner && (
        <div className={styles.settingsPanel}>
          <h3>Настройки чата</h3>
          <div className={styles.settingItem}>
            <label>Максимум участников</label>
            <input type="number" defaultValue={room.max_members || 100} />
          </div>
          <div className={styles.settingItem}>
            <label><input type="checkbox" defaultChecked={room.is_active} /> Активен</label>
          </div>
          <div className={styles.settingActions}>
            <button className={styles.saveBtn}>Сохранить</button>
            <button className={styles.cancelBtn} onClick={() => setShowSettings(false)}>Отмена</button>
          </div>
        </div>
      )}

      {/* ─── Mobile info panel (slide-in overlay) ─── */}
      {showInfoPanel && (
        <div className={styles.infoPanel}>
          {/* Panel header */}
          <div className={styles.infoPanelHeader}>
            <button
              className={styles.infoPanelBack}
              onClick={() => infoPanelSection ? setInfoPanelSection(null) : setShowInfoPanel(false)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              {infoPanelSection ? 'Назад' : 'Вернуться в чат'}
            </button>
            <span className={styles.infoPanelTitle}>
              {infoPanelSection === 'media' ? 'Медиафайлы' :
               infoPanelSection === 'audio' ? 'Аудиосообщения' :
               infoPanelSection === 'members' ? 'Участники' :
               room.name}
            </span>
          </div>

          {/* Main menu — shown when no section selected */}
          {!infoPanelSection && (
            <div className={styles.infoPanelMenu}>
              <button className={styles.infoPanelMenuItem} onClick={() => setInfoPanelSection('media')}>
                <span className={styles.infoPanelMenuIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                </span>
                <span className={styles.infoPanelMenuLabel}>Медиафайлы</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
              </button>
              <button className={styles.infoPanelMenuItem} onClick={() => setInfoPanelSection('audio')}>
                <span className={styles.infoPanelMenuIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M19 10v1a7 7 0 0 1-14 0v-1"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/></svg>
                </span>
                <span className={styles.infoPanelMenuLabel}>Аудиосообщения</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
              </button>
              <button className={styles.infoPanelMenuItem} onClick={() => setInfoPanelSection('members')}>
                <span className={styles.infoPanelMenuIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </span>
                <span className={styles.infoPanelMenuLabel}>Участники</span>
                {members.length > 0 && <span className={styles.infoPanelMenuCount}>{members.length}</span>}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/></svg>
              </button>

              <div className={styles.infoPanelPromo}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                <p>Мы строим отечественный мессенджер, которым вы будете по-настоящему гордиться.</p>
              </div>
            </div>
          )}

          {/* Section content */}
          {infoPanelSection && (
            <div className={styles.infoPanelContent}>
              {infoPanelLoading && <div className={styles.infoPanelLoading}>Загрузка...</div>}

              {/* Media files */}
              {infoPanelSection === 'media' && !infoPanelLoading && (
                infoPanelMedia.length === 0 ? (
                  <div className={styles.infoPanelEmpty}>Нет медиафайлов</div>
                ) : (
                  <div className={styles.mediaGrid}>
                    {infoPanelMedia.map((file, i) => {
                      const isImg = /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/i.test(file.name || '')
                      const isCircle = /^video_message_/i.test(file.name || '')
                      const isVid = !isCircle && /\.(mp4|mov|avi|mkv|m4v|webm)$/i.test(file.name || '')
                      return (
                        <a
                          key={i}
                          className={`${styles.mediaThumb}${isCircle ? ' ' + styles.mediaThumbCircle : ''}`}
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={isImg ? (e) => { e.preventDefault(); setInfoPanelLightbox(file.url) } : undefined}
                        >
                          {isImg ? (
                            <img src={file.url} alt={file.name} />
                          ) : (isCircle || isVid) ? (
                            <video src={file.url} preload="metadata" muted />
                          ) : (
                            <span className={styles.docThumb}>
                              📎<br/>{(file.name || '').split('.').pop()?.toUpperCase()}
                            </span>
                          )}
                        </a>
                      )
                    })}
                  </div>
                )
              )}

              {/* Audio messages */}
              {infoPanelSection === 'audio' && !infoPanelLoading && (
                infoPanelAudio.length === 0 ? (
                  <div className={styles.infoPanelEmpty}>Нет аудиосообщений</div>
                ) : (
                  <div className={styles.audioList}>
                    {infoPanelAudio.map((file, i) => (
                      <div key={i} className={styles.audioItem}>
                        <span className={styles.audioItemIcon}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M19 10v1a7 7 0 0 1-14 0v-1"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/></svg>
                        </span>
                        <audio controls src={file.url} preload="metadata" className={styles.audioPlayer} />
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* Members */}
              {infoPanelSection === 'members' && (
                <div className={styles.infoPanelMembersList}>
                  {loading ? (
                    <div className={styles.infoPanelLoading}>Загрузка...</div>
                  ) : filteredMembers.length === 0 ? (
                    <div className={styles.infoPanelEmpty}>Нет участников</div>
                  ) : (
                    filteredMembers.map((member) => {
                      const memberUser = member.user || member
                      const avatarUrl = memberUser?.avatar
                        ? (memberUser.avatar.startsWith('http') ? memberUser.avatar : `https://security.ncore.kg${memberUser.avatar}`)
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(memberUser?.username || 'User')}&background=7C3AED&color=fff&bold=true`
                      const isRoomOwner = memberUser?.id === room.owner?.id
                      const isCurrentUser = memberUser?.id === currentUser?.id
                      return (
                        <div key={member.id} className={`${styles.memberCard} ${isCurrentUser ? styles.currentUser : ''}`}>
                          <div className={styles.memberAvatarWrap}>
                            <img src={avatarUrl} alt={memberUser?.username} className={styles.memberAvatar}
                              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(memberUser?.username || 'User')}&background=7C3AED&color=fff&bold=true` }} />
                            {memberUser?.is_online && <div className={styles.memberOnlineDot} />}
                          </div>
                          <div className={styles.memberInfo}>
                            <div className={styles.memberName}>
                              {memberUser?.username}
                              {isCurrentUser && <span className={styles.youBadge}>вы</span>}
                            </div>
                            <div className={styles.memberRole}>
                              {isRoomOwner && <span className={styles.ownerBadge}>👑</span>}
                              {member.role === 'moderator' && <span className={styles.modBadge}>🛡️ Мод</span>}
                              {member.role === 'admin' && <span className={styles.adminBadge}>⚔️ Адм</span>}
                              {!isRoomOwner && member.role === 'member' && <span className={styles.regularBadge}>Участник</span>}
                            </div>
                          </div>
                          {isOwner && !isRoomOwner && !isCurrentUser && (
                            <button className={styles.kickBtn} onClick={() => handleKickMember(memberUser.id)} title="Удалить из чата">✕</button>
                          )}
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          )}

          {/* Leave button pinned to bottom of panel */}
          <div className={styles.infoPanelLeaveWrap}>
            <button
              className={styles.infoPanelBackToChatBtn}
              onClick={() => { setShowInfoPanel(false); setInfoPanelSection(null); }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Вернуться в чат
            </button>
            <button className={styles.infoPanelLeaveBtn} onClick={handleLeaveRoom}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Выйти из группы
            </button>
          </div>

          {/* Image lightbox inside info panel */}
          {infoPanelLightbox && (
            <div className={styles.infoPanelLightboxOverlay} onClick={() => setInfoPanelLightbox(null)}>
              <button className={styles.infoPanelLightboxClose} onClick={() => setInfoPanelLightbox(null)}>✕</button>
              <img src={infoPanelLightbox} alt="" onClick={(e) => e.stopPropagation()} className={styles.infoPanelLightboxImg} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ChatRoomModal
